/**
 * useThreadSession: resolve a rail scope's `sessionKey` to a `conversationId`.
 *
 * One durable thread per key: GET `/api/pulse/sessions/by-key?sessionKey=…`
 * resumes the bound session, else POST creates one. `pulse_sessions` upserts
 * on `(user_id, workspace_id, session_key)`, so every mount of the same scope
 * in the same workspace lands on one
 * conversation and its history never fragments.
 *
 * Resolution is lazy (the caller runs `resolve()`), so nothing fans out a
 * session POST until a rail actually mounts.
 */
import { useCallback, useState } from "react";

export interface UseThreadSessionResult {
  conversationId: string | null;
  resolving: boolean;
  error: string | null;
  /** Resolve (idempotently) and return the conversationId. */
  resolve: () => Promise<string | null>;
}

export function useThreadSession(sessionKey: string): UseThreadSessionResult {
  // Cache the resolved conversation TOGETHER with the key it belongs to. A host
  // can keep this hook mounted while the key changes, so a bare id would leak
  // the first scope's thread into every later one; pairing it with its key
  // invalidates the cache the moment the key changes.
  const [resolved, setResolved] = useState<{ key: string; id: string } | null>(null);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only expose the id when it was resolved for the CURRENT key. On a key
  // change this is synchronously null (no stale render, no bleed).
  const conversationId = resolved && resolved.key === sessionKey ? resolved.id : null;

  const resolve = useCallback(async (): Promise<string | null> => {
    if (resolved && resolved.key === sessionKey) return resolved.id;
    setResolving(true);
    setError(null);
    try {
      // Resume first: the same sessionKey always returns the same conversation.
      const getRes = await fetch(
        `/api/pulse/sessions/by-key?sessionKey=${encodeURIComponent(sessionKey)}`,
        { credentials: "include" },
      );
      if (getRes.ok) {
        const { conversationId: id } = (await getRes.json()) as { conversationId: string };
        setResolved({ key: sessionKey, id });
        return id;
      }
      // No bound session yet: create one (ON CONFLICT upsert server-side).
      const postRes = await fetch("/api/pulse/sessions/by-key", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionKey }),
      });
      if (!postRes.ok) throw new Error(`session: ${postRes.status}`);
      const { conversationId: id } = (await postRes.json()) as { conversationId: string };
      setResolved({ key: sessionKey, id });
      return id;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return null;
    } finally {
      setResolving(false);
    }
  }, [resolved, sessionKey]);

  return { conversationId, resolving, error, resolve };
}
